import React, { useEffect, useRef, useState } from "react";
import utils from "../../utils/utils";
import "./style.css";
import LogOut from "../../components/logout/LogOut";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useScreenshot, createFileName } from "use-react-screenshot";

import * as htmlToImage from "html-to-image";
import { Button, Col, Radio, Row, Select, Spin, message } from "antd";
import { _authenticateUser } from "../../redux/actions/authAction";
import plusMinus from "../../assets/icons/plus-minus.png";
import { onSubmitAiCamererieData } from "../../components/https/ai/ai";
import {
  buildInterpretation,
  cameriereTeethList,
  getToothConfidence,
} from "../../helper";

const { Option, OptGroup } = Select;

const AiResponseScreen = () => {
  const screenShotRef = useRef(null);
  const dispatch = useDispatch();
  const teeth = useSelector((state) => state.auth?.teeth);
  const [selectedTeeth, setSelectedTeeth] = useState([]); // Use an array
  const [loading, setLoading] = useState(false);
  const [aiResponseData, setAiResponseData] = useState([]);

  const [enableAutoCallApi, setEnableAutoCallApi] = useState(false);

  const takeScreenShot = async (node) => {
    const dataURI = await htmlToImage.toPng(node, {
      backgroundColor: "white",
      quality: 1,
      //pixelRatio: 1,
    });
    return dataURI;
  };
  const download = (
    image,
    { name = "screen shot", extension = "jpeg" } = {}
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
  };

  const downloadScreenshot = () =>
    takeScreenShot(screenShotRef.current).then(download);

  const aiData = useSelector((state) => state.auth.aiResponse);
  const prefix = "https://";
  const handleSelect = (value, index) => {
    setSelectedTeeth((prev) => {
      const updatedTeeth = [...prev];
      updatedTeeth[index] = value; // Update the specific index
      return updatedTeeth;
    });
  };

  const handleSubmitRecord = async () => {
    // Clean selectedTeeth: remove null/undefined/empty values
    const cleanedSelections = selectedTeeth.filter(
      (t) => t != null && t !== ""
    );

    // Only require tooth numbers for rows with both pulp & tooth areas
    const rowsNeedingSelection = aiData.Data.filter(
      (item) =>
        item.tooth_area_in_pixels !== "None" &&
        item.pulp_area_in_pixels !== "None"
    );

    // Validation: if count mismatch
    if (cleanedSelections.length < rowsNeedingSelection.length) {
      message.error("Please select a tooth for each record.");
      return;
    }

    const teethPayload = aiData.Data.map((item, index) => {
      const selectedId = selectedTeeth[index];
      if (!selectedId) return null; // skip nulls

      const tooth = cameriereTeethList.find(
        (t) => t.fdi == selectedId.toString()
      );

      return tooth
        ? {
            fdi: tooth.fdi,
            pulp_area:
              item.pulp_area_in_pixels === "None"
                ? 0
                : item.pulp_area_in_pixels,
            tooth_area:
              item.tooth_area_in_pixels === "None"
                ? 0
                : item.tooth_area_in_pixels,
          }
        : null;
    }).filter(Boolean); // removes nulls

    if (teethPayload.length === 0) {
      message.error("No valid teeth data to submit.");
      return;
    }

    const payload = {
      gender: "",
      teeth: teethPayload,
    };

    await submitCameriereAiData(payload);
  };

  const submitCameriereAiData = async (payload) => {
    try {
      setLoading(true);
      const response = await onSubmitAiCamererieData({ payload: payload });
      if (response.status === 200) {
        setAiResponseData(response.data.data);
        setLoading(false);
      }

      setLoading(false);
    } catch (error) {
      if (error?.response?.status === 401 || error?.code === "ERR_NETWORK") {
        localStorage.removeItem("user");
        dispatch(_authenticateUser(false));
      }
      message.error(error.response.data.message);
      setLoading(false);
    }
  };

  // const dropdownOptions = createDropdownOptions(teeth);
  const isButtonEnabled = aiData?.Data.some(
    (item) =>
      item.tooth_area_in_pixels !== "None" &&
      item.pulp_area_in_pixels !== "None"
  );

  useEffect(() => {
    if (aiData?.Data?.length > 0) {
      const isAnyToothNumberMissing = aiData?.Data?.some(
        (item) => !item.tooth_no
      );

      if (!isAnyToothNumberMissing) {
        aiData?.Data?.forEach((item, index) => {
          handleSelect(item.tooth_no, index);
        });
        setEnableAutoCallApi(true);
      } else {
        console.log(
          "Tooth number missing in some records, user selection required."
        );
      }
    }
  }, [aiData]);

  useEffect(() => {
    if (enableAutoCallApi) {
      handleSubmitRecord();
    }
  }, [enableAutoCallApi]);

  return (
    <div className="ai__resposne_container" ref={screenShotRef}>
      <div className="ai__resposne_header">
        <div className="ai-header-left">
          <Link to="/">
            <img src={utils.images.applogo} alt="logo" className="ai-logo" />
          </Link>
          <span className="ai-title">Second Opinion</span>
        </div>
        <div style={{ display: "flex" }}>
          <div
            className="screenshot"
            onClick={downloadScreenshot}
            style={{ marginLeft: 20 }}
          >
            <p>Screenshot</p>
          </div>
          <LogOut />
        </div>
      </div>
      <div className="ai__resposne_body">
        <div className="ai__resposne_url">
          <img
            src={`${
              aiData?.["Image Path"]?.startsWith("http")
                ? aiData?.["Image Path"]
                : `https://${aiData?.["Image Path"]}`
            }`}
            alt="url"
          />
        </div>
        <div className="ai__resposne_hostory">
          <h3>Tooth Analysis</h3>

          <table className="ai__selection_table">
            <thead>
              <tr>
                <th style={{ width: "15%" }}>#</th>
                <th>Tooth</th>
                <th>Tooth Area</th>
                <th>Pulp Area</th>
              </tr>
            </thead>
            <tbody>
              {aiData?.Data.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    <Select
                      className="tooth-select"
                      onChange={(value) => handleSelect(value, index)}
                      value={item.tooth_no || item.fdi}
                      virtual={false}
                    >
                      {[
                        "Upper Right Quadrant",
                        "Upper Left Quadrant",
                        "Lower Left Quadrant",
                        "Lower Right Quadrant",
                      ].map((quad) => (
                        <OptGroup key={quad} label={quad}>
                          {cameriereTeethList
                            .filter((tooth) => tooth.quadrant === quad)
                            .map((tooth) => (
                              <Option key={tooth.fdi} value={tooth.fdi}>
                                {tooth.displayName}
                              </Option>
                            ))}
                        </OptGroup>
                      ))}
                    </Select>
                  </td>
                  <td>
                    {!isNaN(item["tooth_area_in_pixels"])
                      ? `${item["tooth_area_in_pixels"]} px`
                      : "N/A"}
                  </td>
                  <td>
                    {!isNaN(item["pulp_area_in_pixels"])
                      ? `${item["pulp_area_in_pixels"]} px`
                      : "N/A"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {aiResponseData.length > 0 && (
            <>
              <h3>Results</h3>
              <table className="ai__results_table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>ISO</th>
                    <th>Tooth</th>
                    <th>Dental Age</th>
                    <th>Range</th>
                    <th>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {aiResponseData.map((item, index) =>
                    item.tooth_area == 0 || item.pulp_area == 0 ? null : (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.fdi || "N/A"}</td>
                        <td>
                          {cameriereTeethList.find(
                            (tooth) => tooth.fdi == item.fdi
                          )?.toothName || "N/A"}
                        </td>
                        <td>{item.dental_age || "N/A"}</td>
                        <td>{item.range || "N/A"}</td>
                        <td>
                          {(() => {
                            const result = getToothConfidence(
                              item.fdi,
                              item.dental_age_numeric
                            );
                            return result?.confidence !== undefined
                              ? result.confidence + "%"
                              : "N/A";
                          })()}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
              <h3>Interpretation: </h3>
              <p>{buildInterpretation(aiResponseData, cameriereTeethList)}</p>
            </>
          )}

          <Row gutter={16} justify="end">
            <Button
              disabled={!isButtonEnabled}
              type="primary"
              onClick={handleSubmitRecord}
              loading={loading}
            >
              {aiResponseData.length > 0 ? "Reassess Age" : "Assess Age"}
            </Button>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default AiResponseScreen;
